import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Sparkles, Upload, MessagesSquare, Bot, Mic, QrCode, BarChart3,
  ArrowRight, Check, Star, ChevronDown, Stethoscope, UtensilsCrossed,
  Briefcase, GraduationCap, MessageCircle, BookOpen, PhoneCall, CalendarClock,
} from "lucide-react";
import { Badge } from "../components/ui/badge";
import RudrasLogo from "../components/RudrasLogo";
import { BUSINESS_IDENTITY } from "../lib/business_identity";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";

/* -----------------------------------------------------------------------------
 * BrandLogo — the single source of truth for the Rudras-AI mark.
 * Emerald-amber chip + Sparkles glyph + "Rudras-AI" wordmark. Matches the
 * BrandCinematic component used on /login + /register so the brand reads
 * the same on every surface.
 * --------------------------------------------------------------------------- */
const BrandLogo = ({ size = "md" }) => {
  const text = size === "sm" ? "text-base" : "text-base sm:text-xl";
  return (
    <Link to="/" className="flex items-center gap-2 flex-shrink-0 group" data-testid="logo">
      <RudrasLogo size={size === "sm" ? "sm" : "md"} />
      <span className={`font-display font-semibold ${text} whitespace-nowrap tracking-tight`}>Rudras-AI</span>
    </Link>
  );
};

/* -----------------------------------------------------------------------------
 * IndustriesMenu — premium emerald dropdown.
 * Healthcare → live Gold Demo chatbot
 * Restaurants → /restaurant marketing page
 * Your Business → /register (start free trial)
 * --------------------------------------------------------------------------- */
const IndustriesMenu = () => (
  <DropdownMenu>
    <DropdownMenuTrigger
      className="inline-flex items-center gap-1.5 text-sm hover:text-emerald-700 transition focus:outline-none data-[state=open]:text-emerald-800"
      data-testid="nav-industries-trigger"
    >
      Industries <ChevronDown className="w-3.5 h-3.5 opacity-70" />
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      sideOffset={10}
      className="w-[300px] p-0 border-emerald-900/15 bg-emerald-950 text-emerald-50 shadow-2xl shadow-emerald-950/30"
      data-testid="nav-industries-menu"
    >
      <DropdownMenuLabel className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.25em] text-emerald-300/70 font-medium">
        Live demos
      </DropdownMenuLabel>
      <DropdownMenuItem asChild className="focus:bg-emerald-900/70 focus:text-amber-200 px-4 py-3 gap-3 cursor-pointer">
        <Link to="/chat/rudras-healthcare-demo" data-testid="nav-industry-healthcare">
          <span className="w-9 h-9 rounded-lg bg-emerald-900/60 ring-1 ring-amber-300/15 grid place-items-center shrink-0">
            <Stethoscope className="w-4 h-4 text-amber-300" />
          </span>
          <span className="flex-1">
            <span className="block font-medium leading-tight">Healthcare</span>
            <span className="block text-[11.5px] text-emerald-300/70 leading-tight mt-0.5">Rudras-AI Healthcare Solutions demo</span>
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-emerald-300/60" />
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild className="focus:bg-emerald-900/70 focus:text-amber-200 px-4 py-3 gap-3 cursor-pointer">
        <Link to="/restaurant" data-testid="nav-industry-restaurants">
          <span className="w-9 h-9 rounded-lg bg-emerald-900/60 ring-1 ring-amber-300/15 grid place-items-center shrink-0">
            <UtensilsCrossed className="w-4 h-4 text-amber-300" />
          </span>
          <span className="flex-1">
            <span className="block font-medium leading-tight">Restaurants</span>
            <span className="block text-[11.5px] text-emerald-300/70 leading-tight mt-0.5">Menu, dietary, and catering Q&amp;A</span>
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-emerald-300/60" />
        </Link>
      </DropdownMenuItem>
      <DropdownMenuSeparator className="bg-emerald-800/40 my-0.5" />
      <DropdownMenuItem asChild className="focus:bg-emerald-900/70 focus:text-amber-200 px-4 py-3 gap-3 cursor-pointer">
        <Link to="/register" data-testid="nav-industry-yours">
          <span className="w-9 h-9 rounded-lg bg-amber-300/15 ring-1 ring-amber-300/30 grid place-items-center shrink-0">
            <Briefcase className="w-4 h-4 text-amber-300" />
          </span>
          <span className="flex-1">
            <span className="block font-medium leading-tight">Your Business</span>
            <span className="block text-[11.5px] text-emerald-300/70 leading-tight mt-0.5">Train your own AI Employee — free trial</span>
          </span>
          <ArrowRight className="w-3.5 h-3.5 text-amber-300/80" />
        </Link>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const Header = () => (
  <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
      <BrandLogo />
      <nav className="hidden md:flex items-center gap-4 lg:gap-7 text-sm">
        <Link to="/features" className="hover:text-emerald-700 transition" data-testid="nav-features">Platform</Link>
        <Link to="/pricing" className="hover:text-emerald-700 transition" data-testid="nav-pricing">Pricing</Link>
        <IndustriesMenu />
      </nav>
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <Link to="/login" className="text-xs sm:text-sm hover:text-emerald-700 transition px-1" data-testid="login-link">Log in</Link>
        <Link to="/register">
          <Button data-testid="header-register-btn" size="sm" className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white text-xs sm:text-sm px-3 sm:px-4 shadow">
            <span className="lg:hidden">Free trial</span>
            <span className="hidden lg:inline">Start free trial</span>
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const Footer = () => (
  <footer className="border-t mt-24 bg-emerald-950 text-emerald-50/90" data-testid="public-footer">
    <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-4 gap-10 text-sm">
      <div className="md:col-span-1">
        <div className="flex items-center gap-2 mb-4">
          <RudrasLogo size="md" ringed className="ring-amber-300/30" />
          <span className="font-display font-semibold text-lg tracking-tight text-white">Rudras-AI</span>
        </div>
        <div className="text-[11px] text-emerald-100/60 mb-2" data-testid="footer-legal-name">
          {BUSINESS_IDENTITY.legalName}
        </div>
        <p className="text-emerald-100/70 leading-relaxed">
          AI Employees trained on your business knowledge — for sales, support,
          onboarding, training, and customer engagement.
        </p>
        <div className="text-[11px] text-emerald-100/60 mt-3 leading-relaxed" data-testid="footer-address">
          {BUSINESS_IDENTITY.addressLines.map((l, i) => <div key={i}>{l}</div>)}
        </div>
      </div>
      <div>
        <div className="font-semibold text-white mb-3 text-[12px] uppercase tracking-[0.18em]">Platform</div>
        <div className="space-y-2 text-emerald-100/70">
          <Link to="/features" className="block hover:text-amber-300 transition">Features</Link>
          <Link to="/pricing" className="block hover:text-amber-300 transition">Pricing</Link>
          <Link to="/chat/rudras-healthcare-demo" className="block hover:text-amber-300 transition">Healthcare demo</Link>
          <Link to="/restaurant" className="block hover:text-amber-300 transition">For restaurants</Link>
          <Link to="/register" className="block hover:text-amber-300 transition">Start free trial</Link>
        </div>
      </div>
      <div>
        <div className="font-semibold text-white mb-3 text-[12px] uppercase tracking-[0.18em]">Legal &amp; Compliance</div>
        <div className="space-y-2 text-emerald-100/70">
          <Link to="/privacy-policy" className="block hover:text-amber-300 transition" data-testid="footer-link-privacy">Privacy Policy</Link>
          <Link to="/terms-of-service" className="block hover:text-amber-300 transition" data-testid="footer-link-terms">Terms of Service</Link>
          <Link to="/sms-program" className="block hover:text-amber-300 transition" data-testid="footer-link-sms-program">SMS / Text Program</Link>
          <Link to="/contact" className="block hover:text-amber-300 transition" data-testid="footer-link-contact">Contact Us</Link>
        </div>
      </div>
      <div>
        <div className="font-semibold text-white mb-3 text-[12px] uppercase tracking-[0.18em]">Contact</div>
        <div className="space-y-2.5 text-emerald-100/80">
          <a href={`mailto:${BUSINESS_IDENTITY.supportEmail}`} className="flex items-center gap-2 hover:text-amber-300 transition" data-testid="footer-email">
            <MessageCircle className="w-3.5 h-3.5 opacity-70" /> {BUSINESS_IDENTITY.supportEmail}
          </a>
          <a href={`tel:${BUSINESS_IDENTITY.phoneE164}`} className="flex items-center gap-2 hover:text-amber-300 transition" data-testid="footer-phone">
            <PhoneCall className="w-3.5 h-3.5 opacity-70" /> {BUSINESS_IDENTITY.phoneDisplay}
          </a>
          <a
            href="https://calendar.app.google/ZZXXWt5D8qXEEDVR7"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-2 rounded-full bg-amber-300/15 hover:bg-amber-300/25 ring-1 ring-amber-300/30 px-3.5 py-1.5 text-amber-200 font-medium transition"
            data-testid="footer-schedule-call"
          >
            <CalendarClock className="w-3.5 h-3.5" /> Schedule a call
          </a>
        </div>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-6 pb-6 pt-2 border-t border-emerald-800/40 text-xs text-emerald-300/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
      <span>© {new Date().getFullYear()} {BUSINESS_IDENTITY.legalName} · d/b/a Rudras-AI · Built on the Intelligence Layer.</span>
      <span>AI responses may be inaccurate; verify important information with the business.</span>
    </div>
  </footer>
);

export function PageShell({ children }) {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * AI Employee preview card — replaces the legacy "Rudras Indian Cuisine"
 * restaurant chat preview. Neutral, enterprise-flavored sales-engineering
 * scenario showing what a Rudras-AI Employee actually does: qualify, answer
 * deeply, propose next step.
 * --------------------------------------------------------------------------- */
const AIEmployeePreview = () => (
  <div className="relative">
    <div className="absolute -inset-4 bg-gradient-to-br from-emerald-600/20 via-amber-300/10 to-emerald-700/20 rounded-3xl blur-2xl" />
    <div className="relative bg-white rounded-2xl border shadow-xl p-5 space-y-3.5">
      <div className="flex items-center gap-3 pb-3 border-b">
        <RudrasLogo size="lg" />
        <div className="leading-tight">
          <div className="font-medium text-sm">Sara · Rudras-AI Sales Engineer</div>
          <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            On call · trained on your knowledge base
          </div>
        </div>
        <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-700/70 font-medium">
          <span className="w-1 h-1 rounded-full bg-emerald-600 animate-pulse" />Live
        </span>
      </div>
      <div className="flex justify-start">
        <div className="bg-secondary rounded-2xl rounded-tl-sm px-3.5 py-2 text-sm max-w-[85%] leading-snug">
          Hi! I work with teams like yours — what are you exploring today?
        </div>
      </div>
      <div className="flex justify-end">
        <div className="bg-emerald-900 text-white rounded-2xl rounded-tr-sm px-3.5 py-2 text-sm max-w-[85%] leading-snug">
          We need an assistant that handles pricing questions + onboarding for 200 clinics.
        </div>
      </div>
      <div className="flex justify-start">
        <div className="bg-secondary rounded-2xl rounded-tl-sm px-3.5 py-2 text-sm max-w-[88%] leading-snug">
          Great fit. For 200 clinics I'd recommend the Group tier with shared training and per-clinic embeds. Want me to share a tailored ROI breakdown?
        </div>
      </div>
      <div className="flex items-center gap-2 pt-2 border-t text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-700" /> Discovery-first</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-700" /> Quote with reason</span>
        <span className="inline-flex items-center gap-1"><Check className="w-3 h-3 text-emerald-700" /> Books the meeting</span>
      </div>
    </div>
  </div>
);

export default function LandingPage() {
  return (
    <PageShell>
      {/* Hero — premium B2B AI Employee positioning */}
      <section className="relative grain-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 sm:pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border bg-card text-xs font-medium mb-6">
              <span className="w-2 h-2 rounded-full bg-emerald-700 animate-pulse-soft" />
              Multi-industry AI Employee Platform
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold leading-[1.04] tracking-tight text-balance">
              AI Employees for Sales, Support, Training & Growth.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl text-balance">
              Train AI Assistants on your PDFs, spreadsheets, JPEGs, and workflows. One platform for customer engagement, onboarding, product education, internal training, and sales enablement — purpose-built for modern businesses.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" target="_blank" rel="noopener noreferrer">
                <Button size="lg" data-testid="hero-cta-create" className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white h-12 px-7 text-base shadow-md">
                  Build my AI Employee <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/chat/rudras-healthcare-demo" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" data-testid="hero-cta-healthcare-demo" className="rounded-full h-12 px-7 text-base border-2 border-emerald-900/30 text-emerald-900 hover:bg-emerald-50">
                  See it in action
                </Button>
              </Link>
              <Link to="/chat/rudras-ai-demo" target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="ghost" data-testid="hero-cta-restaurant-demo" className="rounded-full h-12 px-5 text-base text-emerald-900 hover:bg-emerald-50">
                  Try a B2B demo →
                </Button>
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1.5"><Star className="w-4 h-4 fill-amber-400 text-amber-400" /><span>Free Trial</span></div>
              <div>•</div>
              <div>No credit card required</div>
              <div>•</div>
              <div>Voice + Text</div>
              <div>•</div>
              <div>Multi-tenant ready</div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <AIEmployeePreview />
          </div>
        </div>
      </section>

      {/* Roles strip — what one AI Employee can do */}
      <section className="max-w-7xl mx-auto px-6 pb-10">
        <div className="rounded-2xl border bg-card/60 backdrop-blur-sm px-6 py-5">
          <div className="text-[11px] uppercase tracking-[0.25em] text-muted-foreground mb-3 font-medium">One AI Assistant. Multiple business roles.</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 text-sm">
            {[
              { i: Briefcase, t: "Sales" },
              { i: MessagesSquare, t: "Customer support" },
              { i: GraduationCap, t: "Internal training" },
              { i: BookOpen, t: "Product education" },
              { i: Bot, t: "Onboarding" },
              { i: BarChart3, t: "Workflow guidance" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-foreground/80">
                <span className="w-7 h-7 rounded-md bg-emerald-50 grid place-items-center ring-1 ring-emerald-700/15">
                  <r.i className="w-3.5 h-3.5 text-emerald-800" />
                </span>
                <span className="font-medium tracking-tight">{r.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Upload, title: "Trained on your knowledge", desc: "Drop in PDFs, spreadsheets, and workflows. We chunk, index, and ground every answer in your source material." },
            { icon: MessagesSquare, title: "Lead capture built in", desc: "Capture name, email, phone, intent and full transcripts straight to your CRM or inbox." },
            { icon: Mic, title: "Voice and text in one stack", desc: "Native speech-to-text and text-to-speech for hands-free customer + employee conversations." },
            { icon: GraduationCap, title: "Internal training & onboarding", desc: "Spin up an AI mentor that answers product questions and walks new hires through workflows." },
            { icon: Bot, title: "Industry-aware templates", desc: "Healthcare and restaurant verticals ship pre-shaped; everything else trains in minutes." },
            { icon: BarChart3, title: "Usage analytics", desc: "Track tokens, conversations, leads, escalations, and unanswered questions per AI Employee." },
          ].map((f, i) => (
            <div key={i} className="p-6 rounded-2xl border bg-card hover:border-emerald-700/40 hover:shadow-sm transition-all">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 grid place-items-center mb-4 ring-1 ring-emerald-700/15">
                <f.icon className="w-5 h-5 text-emerald-800" />
              </div>
              <h3 className="font-display font-semibold text-xl mb-2 tracking-tight">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Demo CTA — "Try Your Own AI Employee" */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-3xl border-2 border-emerald-900/15 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 p-8 sm:p-14 grain-bg overflow-hidden">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <Badge className="bg-emerald-900 text-amber-200 hover:bg-emerald-950 mb-4">Free · No signup · 60 seconds</Badge>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold mb-3 text-balance tracking-tight">
                Starts with built-in intelligence. Improves with every interaction.
              </h2>
              <p className="text-muted-foreground text-base sm:text-lg mb-6 max-w-xl leading-relaxed">
                Drop your website or upload your docs. We'll spin up a working AI assistant grounded in your content so you can see exactly how it'd handle real customer conversations.
              </p>
              <Link to="/demo">
                <Button size="lg" data-testid="demo-cta-btn" className="rounded-full bg-emerald-900 hover:bg-emerald-950 text-white h-12 px-7 text-base">
                  Try the AI Employee demo <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-card rounded-2xl border p-5 shadow-sm">
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <div className="w-2 h-2 rounded-full bg-destructive" />
                  <div className="w-2 h-2 rounded-full bg-amber-400" />
                  <div className="w-2 h-2 rounded-full bg-emerald-600" />
                  <span className="ml-2">Rudras-AI · demo console</span>
                </div>
                <div className="space-y-2 font-mono text-xs">
                  <div className="text-muted-foreground">› business: <span className="text-foreground">Rudras-AI Demo</span></div>
                  <div className="text-muted-foreground">› source: <span className="text-foreground">https://rudras-ai.com</span></div>
                  <div className="text-emerald-700">✓ Crawled 18 pages</div>
                  <div className="text-emerald-700">✓ Indexed 231 chunks</div>
                  <div className="text-emerald-700">✓ AI Employee ready in 47s</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="rounded-3xl bg-emerald-950 text-white p-10 sm:p-16 text-center grain-bg overflow-hidden">
          <h2 className="font-display text-4xl sm:text-5xl font-semibold mb-4 text-balance tracking-tight">
            Ready in minutes. Working 24/7.
          </h2>
          <p className="text-emerald-100/80 max-w-xl mx-auto mb-8 leading-relaxed">
            Sign up, upload your knowledge, share the link. Your AI Employee handles the rest — same shift, every shift.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/register">
              <Button size="lg" data-testid="footer-cta" className="rounded-full bg-amber-300 hover:bg-amber-200 text-emerald-950 h-12 px-8 font-semibold shadow-lg">
                Start your free trial
              </Button>
            </Link>
            <a
              href="https://calendar.app.google/ZZXXWt5D8qXEEDVR7"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-cta-schedule"
            >
              <Button size="lg" variant="outline" className="rounded-full h-12 px-8 border-2 border-amber-300/40 text-amber-200 hover:bg-amber-300/10 hover:text-amber-100 bg-transparent">
                <CalendarClock className="mr-2 w-4 h-4" /> Schedule a call
              </Button>
            </a>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
